import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AgentSessionIndexResponse, AgentStreamPageInfo } from '@reflex/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { AxonEvent } from '@/lib/types';
import { mergeEarlierEvents, mergeEarlierPageInfo, normalizeStreamResponse } from './stream-paging';

/**
 * Turn-precise continuous scroll for a paged (indexed) session. The session
 * index gives the client every turn boundary, so instead of asking the
 * server for ~1000-event pages it walks history one turn at a time,
 * keeping the invariant: up to ten turns (capped at ~10k events)
 * buffered above the viewport, ready to roll. A sentinel contact drains
 * the whole buffer into the loaded window in one prepend, so the rendered
 * transcript runs well ahead of the reader.
 *
 * Staged turns live outside the stream query cache until the scroll
 * sentinel makes contact; `commitStagedTurn` then prepends the turn
 * adjacent to the window through the same merge helpers the page-back
 * path uses.
 *
 * Coordination is one explicit state machine (`PagerState`), not flags:
 *
 *   idle ──(idle gate / sentinel contact)──▶ filling
 *   filling ──(sentinel miss)──▶ paging-back ──(window moved)──▶ filling
 *   filling ──(contact, adjacent fetch in flight)──▶ awaiting-commit
 *   awaiting-commit ──(staged)──▶ commit ──▶ filling
 *   awaiting-commit ──(unusable | error)──▶ page-back fallback ──▶ paging-back
 *
 * Only `filling` may dispatch a fetch, and only one at a time — the
 * single-in-flight invariant is a property of the phase, as is the idle
 * gate (`idle` fetches nothing) and the page-back standdown
 * (`paging-back` fetches nothing until the window's start moves). Every
 * fetch outcome (staged, unusable, error) is an explicit transition in
 * {@link settleFetch}; `awaiting-commit` structurally cannot outlive its
 * own fetch, so a sentinel contact can never be parked forever.
 *
 * A commit changes the loaded window's prefix; `useSDKStream` folds just
 * the prepended turns and splices their rows ahead of the mounted model,
 * so a drain costs the new turns, not the whole window.
 */

/**
 * Read-ahead budget: keep MIN(10 turns, 10,000 events) of history staged
 * above the window — the fill walks turn-by-turn and stops at whichever
 * bound the walk hits first (or the floor). Owner's rationale: the
 * session view can fit this, and a sustained scroll should never outrun
 * the buffer. Worst case the buffer holds ~10k events — tens of MB of
 * client memory, bounded, one reference per staged turn (nothing copies
 * the staged arrays until a turn commits into the cache).
 */
export const READAHEAD_MAX_TURNS = 10;
export const READAHEAD_MAX_EVENTS = 10_000;

/**
 * The first fill waits for browser idle so the primary page's heavy first
 * render (reducer fold, tool cards, images) settles before any read-ahead
 * network or parse work dispatches. Browsers without `requestIdleCallback`
 * wait this long instead. A sentinel contact overrides the wait — an
 * actively scrolling reader outranks politeness.
 */
export const READAHEAD_IDLE_FALLBACK_MS = 1000;

export interface TurnRange {
  fromSeq: number;
  toSeq: number;
}

/**
 * The exact range of the turn preceding the loaded window, from two
 * adjacent turn-opening index rows: the greatest turn-opening prompt below
 * `startSeq` opens it, and it runs to `startSeq - 1`. Steer prompts
 * (`opensTurn: false`) get rail entries but are never boundaries. With no
 * opening boundary left above the coverage watermark, the remaining slice
 * down to the watermark (the boot preamble) is the final "turn"; at the
 * watermark, nothing.
 */
export function previousTurnRange(
  index: AgentSessionIndexResponse | undefined,
  startSeq: number,
): TurnRange | null {
  if (!index) return null;
  const watermark = index.sessionIndexFromSeq;
  if (watermark === null || startSeq <= watermark) return null;
  let opening: number | null = null;
  for (const row of index.entries) {
    if (row.entry.type !== 'prompt' || !row.entry.data.opensTurn) continue;
    if (row.seqNumber >= startSeq || row.seqNumber < watermark) continue;
    if (opening === null || row.seqNumber > opening) opening = row.seqNumber;
  }
  return { fromSeq: opening ?? watermark, toSeq: startSeq - 1 };
}

/**
 * The buffer entries still adjacent above the window. The buffer is
 * contiguous, so a bottom the window moved past (a page-back, a jump)
 * makes the whole buffer stale: it reads as empty everywhere and is
 * replaced — not appended to — when the next fetched turn stages.
 */
function liveBuffer(
  staged: StagedTurn[],
  agentId: string,
  current: AgentStreamPageInfo | null | undefined,
): StagedTurn[] {
  const bottom = staged[0];
  if (!bottom || !current?.hasMore) return [];
  if (bottom.agentId !== agentId || bottom.page.endSeq !== current.startSeq - 1) return [];
  return staged;
}

interface StagedTurn {
  agentId: string;
  /**
   * The `fromSeq` the prefetch asked for, kept as the frontier the next
   * deeper fetch walks from — the served page may start higher (the
   * server clamps to its watermark and its hard window cap), and walking
   * from served bounds would refetch a clamped range forever.
   */
  requestedFromSeq: number;
  /**
   * The page info the server actually served for the range — the
   * authority on what was loaded. Its bounds, `hasMore`, and
   * `prevCursor` are what a commit merges into the cache, so a clamped
   * or partial answer never marks unfetched history as loaded.
   */
  page: AgentStreamPageInfo;
  events: AxonEvent[];
}

/** The one fetch the pager may have in flight. */
export interface InflightFetch {
  /** Identity of the request, for settling exactly the fetch that fired. */
  key: string;
  /** The requested top — adjacency to the window is judged on this. */
  toSeq: number;
  /** The frontier the fetch walked from, for the halted-walk memory. */
  frontier: number;
}

/**
 * The pager's coordination state. Each phase owns its invariants:
 * `idle` and `paging-back` never fetch; `filling` fetches at most one
 * range at a time (`haltedAt` remembers a frontier whose answer was
 * unusable or failed, so the walk waits for the frontier to move instead
 * of hot-looping); `awaiting-commit` holds the one fetch a sentinel
 * contact is waiting on and exits only through {@link settleFetch}.
 */
export type PagerState =
  | { phase: 'idle' }
  | { phase: 'filling'; inflight: InflightFetch | null; haltedAt: number | null }
  | { phase: 'awaiting-commit'; inflight: InflightFetch }
  | { phase: 'paging-back'; startSeq: number };

/**
 * How a range fetch resolved: `staged` — a usable envelope whose served
 * range ends where the request did; `unusable` — a legacy-shaped answer
 * (server left paged mode) or a re-clamped range that does not end at the
 * requested top (a request below the server's floor); `error` — the
 * request failed.
 */
export type FetchOutcomeKind = 'staged' | 'unusable' | 'error';

/** What the caller of {@link settleFetch} must do alongside the transition. */
export type SettleAction = 'stage' | 'stage-and-commit' | 'page-back-fallback' | 'drop';

/**
 * The single transition function for a fetch settling, exhaustive over
 * phase × outcome. The invariant the types enforce: a matching fetch
 * settling in `awaiting-commit` NEVER leaves the machine in
 * `awaiting-commit` — it either commits the landed turn or hands the
 * scroll to the page-back fallback, so a `pending` answer to the sentinel
 * can never park the reader forever.
 */
export function settleFetch(
  state: PagerState,
  key: string,
  outcome: FetchOutcomeKind,
  windowStartSeq: number,
): { state: PagerState; action: SettleAction } {
  switch (state.phase) {
    // A fetch from a previous life (agent switch mid-flight) or one the
    // machine already walked away from: the answer is dropped, never
    // staged — `paging-back` owns the network until the window moves.
    case 'idle':
    case 'paging-back':
      return { state, action: 'drop' };
    case 'filling': {
      if (state.inflight?.key !== key) return { state, action: 'drop' };
      switch (outcome) {
        case 'staged':
          return { state: { phase: 'filling', inflight: null, haltedAt: null }, action: 'stage' };
        case 'unusable':
        case 'error':
          // The walk halts at this frontier — no hot-loop refetch — and
          // retries only once the frontier moves (a commit or page-back
          // changed the window). The reader is covered meanwhile: a
          // sentinel contact with nothing in flight is a plain miss.
          return {
            state: { phase: 'filling', inflight: null, haltedAt: state.inflight.frontier },
            action: 'drop',
          };
      }
      break;
    }
    case 'awaiting-commit': {
      if (state.inflight.key !== key) return { state, action: 'drop' };
      switch (outcome) {
        case 'staged':
          return {
            state: { phase: 'filling', inflight: null, haltedAt: null },
            action: 'stage-and-commit',
          };
        case 'unusable':
        case 'error':
          // The awaited fetch cannot deliver: the sentinel was told
          // `pending`, so the machine itself must complete the scroll —
          // explicit fallback to the page-back, which owns the network
          // until the window moves.
          return {
            state: { phase: 'paging-back', startSeq: windowStartSeq },
            action: 'page-back-fallback',
          };
      }
    }
  }
}

/**
 * What a sentinel contact got: `committed` — a staged turn was prepended;
 * `pending` — the adjacent turn's fetch is already in flight and will
 * commit itself on landing, so the caller must NOT start a page-back
 * (two overlapping history fetches); `miss` — nothing staged and nothing
 * in flight, the page-back fallback owns it.
 */
export type CommitStagedTurnResult = 'committed' | 'pending' | 'miss';

/**
 * The single transition function for a sentinel contact, exhaustive over
 * phase. `hasAdjacentBottom` — the buffer's bottom turn sits directly
 * above the window; `inflightAdjacent` — the buffer is empty but the one
 * in-flight fetch is for exactly the adjacent turn.
 */
export function sentinelContact(
  state: PagerState,
  ctx: { hasAdjacentBottom: boolean; inflightAdjacent: boolean; windowStartSeq: number },
): { state: PagerState; result: CommitStagedTurnResult } {
  if (ctx.hasAdjacentBottom) {
    // A hit commits from the buffer in any phase. From `idle` the contact
    // doubles as arming (the reader outranks the politeness gate);
    // `paging-back` stays paging-back — its request is still the only one
    // allowed in flight.
    return {
      state: state.phase === 'idle' ? { phase: 'filling', inflight: null, haltedAt: null } : state,
      result: 'committed',
    };
  }
  switch (state.phase) {
    case 'filling':
      if (state.inflight && ctx.inflightAdjacent) {
        // The adjacent turn is already on its way — smaller and sooner
        // than a page-back. Hold for it; settleFetch guarantees the exit.
        return { state: { phase: 'awaiting-commit', inflight: state.inflight }, result: 'pending' };
      }
      return { state: { phase: 'paging-back', startSeq: ctx.windowStartSeq }, result: 'miss' };
    case 'awaiting-commit':
      // Already holding for the adjacent fetch; nothing new to do.
      return { state, result: 'pending' };
    case 'idle':
      // Nothing fetched yet (the gate has not fired): a plain miss, and
      // entering `paging-back` both arms the machine and hands the fetch
      // to the caller's page-back.
      return { state: { phase: 'paging-back', startSeq: ctx.windowStartSeq }, result: 'miss' };
    case 'paging-back':
      // The page-back is still in flight; re-answering `miss` lets the
      // caller's own isLoadingEarlier guard absorb the repeat contact.
      return { state, result: 'miss' };
  }
}

/** The mutate signature the page-back entry points drive. */
type LoadEarlierFire = (variables?: undefined, options?: { onError?: () => void }) => void;

export interface TurnPager {
  /**
   * Prepend every staged turn adjacent to the window into the stream
   * cache, as one write.
   */
  commitStagedTurn: () => CommitStagedTurnResult;
  /**
   * The one door to the `before`-cursor page-back: transitions the
   * machine to `paging-back` (parking the fill; an in-flight fill fetch
   * settles as a drop by phase rule — no abort needed) and fires the
   * load-earlier mutation. EVERY page-back entry point — the sentinel
   * miss fallback, the load-earlier pill, the rail jump loop — must call
   * this, never the mutation directly, or the fill races the page-back.
   */
  requestPageBack: LoadEarlierFire;
}

export function useTurnPager({
  agentId,
  page,
  index,
  loadEarlier,
}: {
  agentId: string | null;
  /** The loaded window's page info; null/undefined disables the pager. */
  page: AgentStreamPageInfo | null | undefined;
  index: AgentSessionIndexResponse | undefined;
  /**
   * Fire the `before`-cursor page-back fetch (the load-earlier
   * mutation's `mutate`). Only the machine calls this — consumers go
   * through the returned {@link TurnPager.requestPageBack}. Pass a
   * stable function (react-query's `mutate` is): the settle path
   * captures it when a fetch fires.
   */
  loadEarlier: LoadEarlierFire;
}): TurnPager {
  const queryClient = useQueryClient();
  /**
   * The staging buffer, adjacent-first: `staged[0]` sits directly above
   * the loaded window and commits next; each later entry sits directly
   * above the one before it.
   */
  const [staged, setStaged] = useState<StagedTurn[]>([]);
  const [state, setState] = useState<PagerState>({ phase: 'idle' });
  // The machine's authoritative state, written synchronously by every
  // transition: a fetch that resolves on a microtask settles against the
  // transition that fired it even before React re-renders (the `state`
  // value only drives effects). All transitions go through `applyState`.
  const stateRef = useRef<PagerState>({ phase: 'idle' });
  const applyState = useCallback((next: PagerState) => {
    stateRef.current = next;
    setState(next);
  }, []);
  // No agent-switch reset: the stream view keys its whole subtree by
  // agent id, so this hook remounts per agent — `agentId` only ever
  // toggles between one id and null here. The per-agent fetch keys and
  // the `agentId` carried on staged turns stay as defense-in-depth.

  // The idle gate: `idle` exits to `filling` only once the browser
  // reports idle (or after the fallback delay), giving the primary page's
  // first render exclusive use of the network and main thread.
  useEffect(() => {
    if (!agentId || state.phase !== 'idle') return;
    const arm = () => applyState({ phase: 'filling', inflight: null, haltedAt: null });
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(arm);
      return () => cancelIdleCallback(handle);
    }
    const timer = setTimeout(arm, READAHEAD_IDLE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [agentId, state.phase, applyState]);

  /**
   * Prepend a contiguous run of turns (adjacent-first, as the buffer is
   * ordered) through the shared merge path in ONE cache write. Every
   * cache write is a render and a fold downstream, so a drain of the
   * buffer is priced as a single prepend, not one per turn.
   */
  const commitTurnsToCache = useCallback(
    (turns: readonly StagedTurn[]) => {
      const top = turns[turns.length - 1];
      if (!top) return;
      // Transcript order: the topmost (deepest) turn's events come first.
      const events = [...turns].reverse().flatMap((turn) => turn.events);
      queryClient.setQueryData<AxonEvent[]>([...queryKeys.stream(top.agentId)], (old) =>
        mergeEarlierEvents(old ?? [], events),
      );
      // The topmost served page info carries the truth about what is now
      // loaded: a clamped start keeps `hasMore`/`prevCursor` honest, so
      // history the clamp withheld stays reachable instead of vanishing
      // behind a premature end of pagination.
      queryClient.setQueryData<AgentStreamPageInfo | null>(
        [...queryKeys.streamPage(top.agentId)],
        (old) => (old ? mergeEarlierPageInfo(old, top.page) : old),
      );
    },
    [queryClient],
  );

  /** Settle a fetch against the machine and perform the decided action. */
  const settle = useCallback(
    (key: string, outcome: FetchOutcomeKind, turn: StagedTurn | null, frontierAtFire: number) => {
      const current = agentId
        ? queryClient.getQueryData<AgentStreamPageInfo | null>([...queryKeys.streamPage(agentId)])
        : null;
      const { state: next, action } = settleFetch(
        stateRef.current,
        key,
        outcome,
        current?.startSeq ?? 0,
      );
      applyState(next);
      switch (action) {
        case 'drop':
          return;
        case 'stage':
          if (turn) {
            setStaged((prev) => {
              // Append only if the live buffer still ends where this
              // fetch extended it; a buffer the window moved past reads
              // as empty and is replaced outright. A frontier that no
              // longer matches drops the answer — the next pass
              // refetches against the new frontier.
              const base = agentId ? liveBuffer(prev, agentId, current) : [];
              const prevTop = base[base.length - 1];
              const prevFrontier = prevTop?.requestedFromSeq ?? current?.startSeq ?? null;
              if (prevFrontier !== frontierAtFire) return prev;
              return [...base, turn];
            });
          }
          return;
        case 'stage-and-commit':
          if (turn && current?.hasMore && turn.page.endSeq === current.startSeq - 1) {
            // Deliver the sentinel contact that was answered `pending`:
            // the landed turn goes straight into the cache, no buffer
            // stop. The viewport anchor captured at contact compensates
            // the prepend exactly as a buffered commit would.
            commitTurnsToCache([turn]);
          } else if (turn) {
            // The window moved while the fetch was in flight (a jump):
            // the contact's intent is gone; stage it and let the normal
            // staleness pruning decide.
            setStaged((prev) => (prev.length === 0 ? [turn] : prev));
          }
          return;
        case 'page-back-fallback':
          // The machine promised the sentinel a delivery and the fetch
          // could not provide one: complete the scroll via the page-back.
          // The settleFetch transition already entered `paging-back`, so
          // the raw fetch fires directly — no second transition.
          loadEarlier();
          return;
      }
    },
    [agentId, queryClient, commitTurnsToCache, applyState, loadEarlier],
  );

  // The one public door to the page-back (pill, sentinel miss fallback,
  // rail jump): park the machine first, then fire. Entering `paging-back`
  // means any fill fetch still in flight settles as a drop — the phase
  // rule, not an abort — and the fill resumes only once the page-back
  // moves the window.
  const requestPageBack = useCallback<LoadEarlierFire>(
    (variables, options) => {
      if (agentId) {
        const current = queryClient.getQueryData<AgentStreamPageInfo | null>([
          ...queryKeys.streamPage(agentId),
        ]);
        if (current?.hasMore) {
          applyState({ phase: 'paging-back', startSeq: current.startSeq });
        }
      }
      loadEarlier(variables, options);
    },
    [agentId, queryClient, applyState, loadEarlier],
  );

  // The fill loop: only `filling` with nothing in flight may dispatch,
  // one turn per pass — each settled fetch re-runs the effect (its
  // transition applies through `applyState` in the promise callback) for
  // the next deeper turn until the budget, the floor, a clamp, or a
  // halted frontier stops the walk. `paging-back` exits here once the
  // window's start moves (the page-back landed), refilling against the
  // new window. The effect decides against and records into `stateRef`
  // directly — the authoritative machine — so dispatching a fetch never
  // needs a same-effect setState; the React `state` dep only re-runs the
  // pass after each async transition.
  useEffect(() => {
    if (!agentId || !page?.hasMore || !index) return;
    // The `page` prop lags the cache for a beat after a commit: the
    // cached page info is the authority on where the window starts now.
    const current = queryClient.getQueryData<AgentStreamPageInfo | null>([
      ...queryKeys.streamPage(agentId),
    ]);
    if (!current?.hasMore) return;
    // `paging-back` exits when the window's start moved (the page-back
    // landed): resolve it to a fresh fill inline, recorded with the
    // dispatch below.
    let phase: PagerState = stateRef.current;
    if (phase.phase === 'paging-back') {
      if (phase.startSeq === current.startSeq) return;
      phase = { phase: 'filling', inflight: null, haltedAt: null };
      stateRef.current = phase;
    }
    if (phase.phase !== 'filling' || phase.inflight !== null) return;
    // A buffer the window moved past reads as empty (see liveBuffer) —
    // the walk restarts from the window and the stage path replaces the
    // stale entries when the fresh turn lands.
    const buffer = liveBuffer(staged, agentId, current);
    const top = buffer[buffer.length - 1];
    // A clamped answer (served start above the requested start) means the
    // server withheld deeper history: walking further would gap the
    // buffer or loop on the clamp, so the fill stops here.
    if (top && (top.page.startSeq > top.requestedFromSeq || !top.page.hasMore)) return;
    const bufferEvents = buffer.reduce((total, turn) => total + turn.events.length, 0);
    if (buffer.length >= READAHEAD_MAX_TURNS || bufferEvents >= READAHEAD_MAX_EVENTS) return;
    const frontier = top ? top.requestedFromSeq : current.startSeq;
    // A previous walk from this exact frontier got an unusable answer or
    // failed: wait for the frontier to move instead of hot-looping.
    if (phase.haltedAt === frontier) return;
    const target = previousTurnRange(index, frontier);
    if (!target) return;
    const key = `${agentId}:${target.fromSeq}-${target.toSeq}`;
    // Recorded synchronously in the ref (no render needed to remember an
    // in-flight fetch): a re-run of this effect sees it and skips, and a
    // fetch that resolves on a microtask settles against it.
    stateRef.current = {
      phase: 'filling',
      inflight: { key, toSeq: target.toSeq, frontier },
      haltedAt: null,
    };
    void api
      .getStreamRange(agentId, target.fromSeq, target.toSeq)
      .then((response) => {
        // A legacy-shaped answer (server left paged mode) or a served
        // range that does not end where the request did (a request below
        // the server's floor comes back re-clamped; the top is never
        // clamped otherwise) cannot be staged.
        const { events, page: rangePage } = normalizeStreamResponse(response);
        if (!rangePage || rangePage.endSeq !== target.toSeq) {
          settle(key, 'unusable', null, frontier);
          return;
        }
        settle(
          key,
          'staged',
          { agentId, requestedFromSeq: target.fromSeq, page: rangePage, events },
          frontier,
        );
      })
      .catch(() => {
        settle(key, 'error', null, frontier);
      });
  }, [agentId, page, index, state, staged, queryClient, settle, applyState]);

  const commitStagedTurn = useCallback((): CommitStagedTurnResult => {
    if (!agentId) return 'miss';
    const current = queryClient.getQueryData<AgentStreamPageInfo | null>([
      ...queryKeys.streamPage(agentId),
    ]);
    if (!current?.hasMore) return 'miss';
    const bottom = staged[0];
    // Only an adjacent buffer may commit: a page-back or jump that
    // already moved the window past the staged range makes it stale.
    // Adjacency is judged on the served bounds — the server never clamps
    // the top of a range, so a served page always ends where the request
    // did.
    const hasAdjacentBottom =
      !!bottom && bottom.agentId === agentId && bottom.page.endSeq === current.startSeq - 1;
    const st = stateRef.current;
    const inflightAdjacent =
      staged.length === 0 &&
      (st.phase === 'filling' || st.phase === 'awaiting-commit') &&
      st.inflight !== null &&
      st.inflight.toSeq === current.startSeq - 1;
    const { state: next, result } = sentinelContact(st, {
      hasAdjacentBottom,
      inflightAdjacent,
      windowStartSeq: current.startSeq,
    });
    applyState(next);
    if (result === 'committed' && bottom) {
      // Drain the whole adjacent buffer: the sentinel sits a lookahead
      // margin above the viewport, so everything staged is rendered ahead
      // of the reader in one prepend instead of one turn per contact.
      const run = liveBuffer(staged, agentId, current);
      commitTurnsToCache(run);
      // Consuming the run leaves the window start at its top and re-arms
      // the fill from there.
      setStaged((prev) => prev.filter((turn) => !run.includes(turn)));
    }
    return result;
  }, [agentId, staged, queryClient, commitTurnsToCache, applyState]);

  return { commitStagedTurn, requestPageBack };
}
